import type {
  DebtDto,
  DebtListResponse,
  CreateDebtRequest,
  UpdateDebtRequest,
  SettleDebtRequest,
} from '@signalvote/shared';
import { failureFromResponse } from '@signalvote/shared';
import { apiJson, type ApiErrorBody } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite, edit } from '../../lib/data';

/** The server's sentence for a refusal (an empty one counts as none), else the fallback. */
const refusal = (fallback: string) => (body: ApiErrorBody, response: Response) =>
  failureFromResponse(response, body, typeof body.error === 'string' && body.error !== '' ? body.error : fallback);

// ADR-031 (GitHub #92) — the non-loan Schedule D debt write-path (federal-only). A debt is a
// living treasurer-maintained obligation owed BY the committee: recorded once, then paid down
// by tagging ordinary expenditures (Expenditure.DebtId) — its SD10 begin/incurred/payment/end
// columns and Form 3 line 10 are computed from those dated payments, never a stored balance.

/** The campaign's debts, newest incur date first. */
export const useFinanceDebts = defineQuery({
  key: () => ['finance', 'debts'],
  reads: 'GET /api/finance/debts',
  fetch: async (_: undefined, { campaignId }): Promise<DebtDto[]> => {
    const body = await apiJson<DebtListResponse>('/api/finance/debts', {
      headers: campaignHeaders(campaignId),
      error: 'Failed to load debts',
    });
    return body.items;
  },
});

/** Show a debt voided (or restored) in the list before the server answers. */
const markVoided = (id: string, voided: boolean) => [
  edit(useFinanceDebts, undefined, (debts) =>
    debts.map((d) => (d.id === id ? { ...d, voidedAtUtc: voided ? new Date().toISOString() : null } : d)),
  ),
];

/** POST /api/finance/debts — record a new debt owed BY the committee (a non-cash gesture). */
export const useCreateDebt = defineWrite({
  send: (data: CreateDebtRequest) =>
    apiJson<DebtDto>('/api/finance/debts', { method: 'POST', json: data, error: refusal('Failed to record debt') }),
});

/**
 * PATCH /api/finance/debts/{id} — edit a debt's incurred amount / date / purpose (and notes).
 * Amount/date/purpose are locked once the incurred period is filed (reopen + amend); notes stay
 * editable regardless.
 */
export const useUpdateDebt = defineWrite({
  send: ({ id, data }: { id: string; data: UpdateDebtRequest }) =>
    apiJson<DebtDto>(`/api/finance/debts/${id}`, {
      method: 'PATCH',
      json: data,
      error: refusal('Failed to update debt'),
    }),
});

/**
 * POST /api/finance/debts/{id}/void. Voids a debt entered in error so it drops from a fresh
 * filing's Schedule D / line 10 (kept for audit). A debt in a filed, still-locked period
 * returns a 400 the caller surfaces. A paid-off debt is NEVER voided — it self-suppresses.
 */
export const useVoidDebt = defineWrite({
  send: (id: string) =>
    apiJson<DebtDto>(`/api/finance/debts/${id}/void`, { method: 'POST', error: refusal('Failed to void debt') }),
  patch: (id) => markVoided(id, true),
});

/** POST /api/finance/debts/{id}/unvoid. Restores a voided debt (the inverse). */
export const useUnvoidDebt = defineWrite({
  send: (id: string) =>
    apiJson<DebtDto>(`/api/finance/debts/${id}/unvoid`, { method: 'POST', error: refusal('Failed to restore debt') }),
  patch: (id) => markVoided(id, false),
});

/**
 * POST /api/finance/debts/{id}/settle. Records a debt as settled for less than its face amount,
 * or forgiven by the creditor, capturing what they accepted and why (52 U.S.C. 30104(b)(8)). The
 * incurred amount is untouched — the remainder is written off as of the settlement date, which is
 * what takes the debt out of Schedule D and line 10 from that period on. A settlement dated into
 * a filed, still-locked period returns a 400 the caller surfaces, as does an amount the debt's
 * recorded payments do not support.
 */
export const useSettleDebt = defineWrite({
  send: ({ id, data }: { id: string; data: SettleDebtRequest }) =>
    apiJson<DebtDto>(`/api/finance/debts/${id}/settle`, {
      method: 'POST',
      json: data,
      error: refusal('Failed to record the settlement'),
    }),
});

/** POST /api/finance/debts/{id}/unsettle. Undoes a settlement recorded in error (the inverse). */
export const useUnsettleDebt = defineWrite({
  send: (id: string) =>
    apiJson<DebtDto>(`/api/finance/debts/${id}/unsettle`, {
      method: 'POST',
      error: refusal('Failed to undo the settlement'),
    }),
});
